import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime } from '@/i18n/format';
import { summarizeDiff } from '@/lib/revisions';
import type { RequestRevision } from '@/api/types';

/**
 * "Last change" widget of a request that is already at the warehouse: when it was last edited (and by whom, when the
 * server says so), a badge once it was changed after being sent, and the collapsible change history (what changed in
 * each revision). Revision 1 is the version that was sent; it carries no diff.
 */
export function RevisionInfo({
  revisionNo,
  lastEditedAt,
  lastEditedBy,
  revisions,
}: {
  readonly revisionNo: number | undefined;
  readonly lastEditedAt: string | null | undefined;
  readonly lastEditedBy: string | null | undefined;
  readonly revisions: readonly RequestRevision[] | undefined;
}): JSX.Element {
  const { t, locale } = useLanguage();
  const edited = (revisionNo ?? 1) > 1;
  const history = revisions ?? [];
  const when = lastEditedAt ? formatDateTime(lastEditedAt, locale) : '';
  return (
    <div className="revision-info" data-testid="revision-info">
      <p className="revision-last" data-testid="revision-last">
        {lastEditedAt ? (lastEditedBy ? t('revise.lastEditedBy', { when, who: lastEditedBy }) : t('revise.lastEdited', { when })) : t('revise.neverEdited')}
        {edited && <span className="revision-badge" data-testid="revision-badge">{t('revise.editedBadge')}</span>}
      </p>
      {history.length > 1 && (
        <details className="revision-history" data-testid="revision-history">
          <summary>{t('revise.history')} ({history.length - 1})</summary>
          <ol>
            {history.map((r) => (
              <li key={r.revision_no}>
                <div className="rev-head">
                  {t('revise.rev', { n: r.revision_no })} · {formatDateTime(r.at, locale)}
                  {r.revision_no > 1 && ` · ${r.actor_name ?? (r.actor_kind === 'OFFICE' ? t('revise.byWarehouse') : '')}`.replace(/ · $/, '')}
                </div>
                {r.revision_no === 1 ? (
                  <div className="rev-line">{t('revise.revSubmitted')}</div>
                ) : (
                  <ul>
                    {summarizeDiff(t, locale, r.diff).map((line, i) => (
                      <li key={i} className="rev-line">{line}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
